import {
    Component,
    Input,
    OnInit,
    OnDestroy,
    ChangeDetectorRef,
    ViewChild
} from '@angular/core';
import { Observable, Subscription } from 'rxjs/Rx';
import "rxjs/add/observable/timer";

import { VULNERABILITY_SCAN_STATUS } from '../utils';
import {
    VulnerabilitySummary,
    TagService,
    ScanningResultService,
    Tag
} from '../service/index';
import { ErrorHandler } from '../error-handler/index';
import { toPromise } from '../utils';
import { ChannelService } from '../channel/index';
import { JobLogViewerComponent } from '../job-log-viewer/index';

const STATE_CHECK_INTERVAL: number = 2000; // 2s
const RETRY_TIMES: number = 3;

@Component({
    selector: 'hbr-vulnerability-bar',
    template: `<div class="bar-wrapper">
    <div *ngIf="stopped" class="bar-state">
       <span class="label">{{'VULNERABILITY.STATE.STOPPED' | translate}}</span>
    </div>
    <div *ngIf="queued" class="bar-state">
        <span class="label label-orange">{{'VULNERABILITY.STATE.QUEUED' | translate}}</span>
    </div>
    <div *ngIf="error" class="bar-state bar-state-error">
        <a href="javascript:void(0);" class="error-text" (click)="viewLog()">
            <clr-icon shape="error" class="is-error" size="24"></clr-icon>
            {{'VULNERABILITY.STATE.ERROR' | translate}}
        </a>
    </div>
    <div *ngIf="scanning" class="bar-state bar-state-chart">
        <div>{{'VULNERABILITY.STATE.SCANNING' | translate}}</div>
        <div class="progress loop" style="height:2px;"><progress></progress></div>
    </div>
    <div *ngIf="completed" class="bar-state bar-state-chart">
        <hbr-vulnerability-summary-chart [summary]="summary"></hbr-vulnerability-summary-chart>
    </div>
    <div *ngIf="unknown" class="bar-state">
        <clr-icon shape="warning" class="is-warning" size="24"></clr-icon>
        <span style="margin-left:-5px;">{{'VULNERABILITY.STATE.UNKNOWN' | translate}}</span>
    </div>
    <job-log-viewer #scanningLogViewer [jobType]="'scan'"></job-log-viewer>
</div>`,
    styles: [`.bar-wrapper{width:120px;height:12px}.bar-state{text-align:center!important}.bar-state-chart{margin-top:2px}.bar-state-error{position:relative;top:-4px}.error-text{position:relative;top:1px;margin-left:-5px;cursor:pointer}.scanning-button{height:24px;margin-top:0;margin-bottom:0;vertical-align:middle;top:-12px;position:relative}.tip-wrapper{display:inline-block;height:10px;max-width:120px}.tip-position{margin-left:-4px}.tip-block{margin-left:-3px}.bar-block-high{background-color:#e62700}.bar-block-medium{background-color:orange}.bar-block-low{background-color:#ff0}.bar-block-none{background-color:green}.bar-block-unknown{background-color:grey}.bar-tooltip-font{font-size:13px;color:#fff}.bar-tooltip-font-title{font-weight:600}.bar-summary{margin-top:12px;text-align:left}.bar-scanning-time{margin-top:12px}.bar-summary-item{margin-top:3px;margin-bottom:3px}.bar-summary-item span:nth-child(1){width:30px;text-align:center;display:inline-block}.bar-summary-item span:nth-child(2){width:28px;display:inline-block}.option-right{padding-right:16px}.refresh-btn{cursor:pointer}.refresh-btn:hover{color:#007cbb}.label.label-medium{background-color:#ffe4a9;border:1px solid orange;color:orange}.tip-icon-medium{color:orange}.label.label-low{background:rgba(251,255,0,.38);color:#c5c50b;border:1px solid #e6e63f}.tip-icon-low{color:#ff0}.font-color-green{color:green}.bar-tooltip-font-larger span{font-size:16px;vertical-align:middle}hr{border-bottom:0;border-color:#aaa;margin:6px -10px}.font-weight-600{font-weight:600}.rightPos{position:absolute;z-index:100;right:35px;margin-top:4px}`]
})
export class ResultBarChartComponent implements OnInit, OnDestroy {
    @Input() repoName: string = "";
    @Input() tagId: string = "";
    @Input() summary: VulnerabilitySummary;
    onSubmitting: boolean = false;
    retryCounter: number = 0;
    stateCheckTimer: Subscription;
    scanSubscription: Subscription;
    timerHandler: any;

    @ViewChild("scanningLogViewer")
    scanningJobLogViewer: JobLogViewerComponent;

    constructor(
        private tagService: TagService,
        private scanningService: ScanningResultService,
        private errorHandler: ErrorHandler,
        private channel: ChannelService,
        private ref: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.scanSubscription = this.channel.scanCommand$.subscribe((tagId: string) => {
            let myFullTag: string = this.repoName + "/" + this.tagId;
            if (myFullTag === tagId) {
                this.scanNow();
            }
        });
    }

    ngOnDestroy(): void {
        if (this.stateCheckTimer) {
            this.stateCheckTimer.unsubscribe();
            this.stateCheckTimer = null;
        }
        if (this.scanSubscription) {
            this.scanSubscription.unsubscribe();
        };
    }

    // Get vulnerability scanning status
    public get status(): string {
        if (this.summary && this.summary.scan_status) {
            return this.summary.scan_status;
        }

        return VULNERABILITY_SCAN_STATUS.stopped;
    }

    public get completed(): boolean {
        return this.status === VULNERABILITY_SCAN_STATUS.finished;
    }

    public get error(): boolean {
        return this.status === VULNERABILITY_SCAN_STATUS.error;
    }

    public get queued(): boolean {
        return this.status === VULNERABILITY_SCAN_STATUS.pending;
    }

    public get scanning(): boolean {
        return this.status === VULNERABILITY_SCAN_STATUS.running;
    }

    public get stopped(): boolean {
        return this.status === VULNERABILITY_SCAN_STATUS.stopped;
    }

    public get unknown(): boolean {
        return this.status === VULNERABILITY_SCAN_STATUS.unknown;
    }

    scanNow(): void {
        if (this.onSubmitting) {
            // Avoid duplicated submitting
            return;
        }

        if (!this.repoName || !this.tagId) {
            return;
        }

        this.onSubmitting = true;

        toPromise<any>(this.scanningService.startVulnerabilityScanning(this.repoName, this.tagId))
            .then(() => {
                this.onSubmitting = false;

                // Forcely change status to queued after successful submitting
                this.summary = {
                    scan_status: VULNERABILITY_SCAN_STATUS.pending,
                    severity: null,
                    components: null,
                    update_time: null
                };

                // Forcely refresh view
                this.forceRefreshView(1000);

                // Start check status util the job is done
                if (!this.stateCheckTimer) {
                    // Avoid duplicated subscribing
                    this.stateCheckTimer = Observable.timer(STATE_CHECK_INTERVAL, STATE_CHECK_INTERVAL).subscribe(() => {
                        this.getSummary();
                    });
                }
            })
            .catch(error => {
                this.onSubmitting = false;
                this.errorHandler.error(error);
            });
    }

    getSummary(): void {
        if (!this.repoName || !this.tagId) {
            return;
        }

        toPromise<Tag>(this.tagService.getTag(this.repoName, this.tagId))
            .then((t: Tag) => {
                // To keep the same summary reference, use value copy.
                this.copyValue(t.scan_overview);

                // Forcely refresh view
                this.forceRefreshView(1000);

                if (!this.queued && !this.scanning) {
                    // Scanning should be done
                    if (this.stateCheckTimer) {
                        this.stateCheckTimer.unsubscribe();
                        this.stateCheckTimer = null;
                    }
                }
            })
            .catch(error => {
                this.errorHandler.error(error);
                this.retryCounter++;
                if (this.retryCounter >= RETRY_TIMES) {
                    // Stop timer
                    if (this.stateCheckTimer) {
                        this.stateCheckTimer.unsubscribe();
                        this.stateCheckTimer = null;
                    }
                    this.retryCounter = 0;
                }
            });
    }

    copyValue(newVal: VulnerabilitySummary): void {
        if (!this.summary || !newVal || !newVal.scan_status) { return; }
        this.summary.scan_status = newVal.scan_status;
        this.summary.job_id = newVal.job_id;
        this.summary.severity = newVal.severity;
        this.summary.components = newVal.components;
        this.summary.update_time = newVal.update_time;
    }

    forceRefreshView(duration: number): void {
        // Reset timer
        if (this.timerHandler) {
            clearInterval(this.timerHandler);
        }
        this.timerHandler = setInterval(() => this.ref.markForCheck(), 100);
        setTimeout(() => {
            if (this.timerHandler) {
                clearInterval(this.timerHandler);
                this.timerHandler = null;
            }
        }, duration);
    }

    // Check error log
    viewLog(): void {
        if (this.summary && this.summary.job_id) {
            this.scanningJobLogViewer.open(this.summary.job_id);
        }
    }
}
